export type WidgetConfig = {
    cartBackendBaseUrl: string
    editorBaseUrl: string
    graphqlUrl: string
    editorDirectUploadMode?: 'auto' | 'prefer' | 'require'
    editorDirectPresignBatchSize?: number
    editorDirectPutConcurrency?: number
    authToken?: string
    quickUploadTargetUrl?: string
    mobileUploadUrl?: string
    mercureHubUrl?: string
    cartUrl?: string
    shippingCostsUrl?: string
    overviewUrl?: string
    logoUrl?: string
    fontFamily?: string
    fontSizeBase?: string
    textColor?: string
    mutedColor?: string
    accentColor?: string
    backgroundColor?: string
    borderColor?: string
    hideWhenEmpty?: boolean
    clearCartOnMount?: boolean
    quickUploadProjectCode?: string
    quickUploadSizeVariantId?: string
    quickUploadSizeTitle?: string
    quickUploadSizeWidthMm?: string | number
    quickUploadSizeHeightMm?: string | number
    simpleUploadFlow?: boolean
    simpleEditorFlow?: boolean
    minimalUploadFlow?: boolean
    quickUploadQuantity?: string | number
    quickUploadGrayscale?: string | number | boolean
    quickUploadCropRequired?: string | number | boolean
    quickUploadRecoveryState?: string
}
// widget-entry / public API
export type EntryContext = {
    parentId?: number
    parentName?: string

    categoryId?: number
    categoryName?: string

    productId?: number
    productName?: string

}

export type WidgetProps = {
    editorBaseUrl: string
    cartBackendBaseUrl: string
    graphqlUrl: string
    editorDirectUploadMode?: 'auto' | 'prefer' | 'require'
    editorDirectPresignBatchSize?: number
    editorDirectPutConcurrency?: number
    authToken?: string

    routerMode?: 'memory' | 'hash'
    routerBase?: string
    /** Einstieg */
    entry?: EntryContext

    /** Direkt in Quick Upload starten */
    quickUpload?: boolean
    quickUploadTargetUrl?: string // absolute URL to main editor
    quickUploadProjectCode?: string
    quickUploadSizeVariantId?: string
    quickUploadSizeTitle?: string
    quickUploadSizeWidthMm?: string | number
    quickUploadSizeHeightMm?: string | number
    simpleUploadFlow?: boolean
    simpleEditorFlow?: boolean
    minimalUploadFlow?: boolean
    quickUploadQuantity?: string | number
    quickUploadGrayscale?: string | number | boolean
    quickUploadCropRequired?: string | number | boolean
    quickUploadRecoveryState?: string
    mobileUploadUrl?: string
    mercureHubUrl?: string
    payload?: string

    /** optional */
    initialRoute?: string
    cartUrl?: string
    shippingCostsUrl?: string
    overviewUrl?: string
    logoUrl?: string
    fontFamily?: string
    fontSizeBase?: string
    textColor?: string
    mutedColor?: string
    accentColor?: string
    backgroundColor?: string
    borderColor?: string
    hideWhenEmpty?: boolean
    clearCartOnMount?: boolean
}

const DEFAULT_GRAPHQL_URL = 'https://www.pixelfoto-express.de/graphql'
const DEFAULT_EDITOR_BASE_URL = 'https://www.pixelfoto-express.de/editor'
const DEFAULT_LOGO_URL = 'https://www.pixelfoto-express.de/page-assets/media/brand.svg'
const DEFAULT_OVERVIEW_URL = '/'

let _cfg: WidgetConfig | null = null

function trimTrailingSlash(value: string) {
    return value.replace(/\/+$/, '')
}

function resolveAbsoluteUrl(value: string) {
    const raw = String(value || '').trim()
    if (!raw) return ''
    if (/^https?:\/\//i.test(raw)) return trimTrailingSlash(raw)
    if (typeof window === 'undefined') return trimTrailingSlash(raw)
    try {
        return trimTrailingSlash(new URL(raw, window.location.origin).toString())
    } catch {
        return trimTrailingSlash(raw)
    }
}

function normalizeCartBackendBaseUrl(value: string) {
    const raw = trimTrailingSlash(String(value || '').trim())
    if (!raw) return raw
    return /\/v\d+$/i.test(raw) ? raw : `${raw}/v1`
}

export function setWidgetConfig(cfg: Partial<WidgetConfig>) {
    const graphqlUrl =
        typeof cfg.graphqlUrl === 'string' && cfg.graphqlUrl.trim()
            ? resolveAbsoluteUrl(cfg.graphqlUrl)
            : DEFAULT_GRAPHQL_URL

    const cartBackendBaseUrl = normalizeCartBackendBaseUrl(resolveAbsoluteUrl(String(cfg.cartBackendBaseUrl || '').trim()))
    const editorBaseUrl =
        typeof cfg.editorBaseUrl === 'string' && cfg.editorBaseUrl.trim()
            ? resolveAbsoluteUrl(cfg.editorBaseUrl)
            : DEFAULT_EDITOR_BASE_URL

    _cfg = {
        ...cfg,
        cartBackendBaseUrl,
        editorBaseUrl,
        graphqlUrl,
        mercureHubUrl:
            typeof cfg.mercureHubUrl === 'string' && cfg.mercureHubUrl.trim()
                ? resolveAbsoluteUrl(cfg.mercureHubUrl)
                : undefined,
        overviewUrl:
            typeof cfg.overviewUrl === 'string' && cfg.overviewUrl.trim()
                ? cfg.overviewUrl.trim()
                : DEFAULT_OVERVIEW_URL,
        logoUrl:
            typeof cfg.logoUrl === 'string' && cfg.logoUrl.trim()
                ? cfg.logoUrl.trim()
                : DEFAULT_LOGO_URL,
    } as WidgetConfig
}

export function getWidgetConfig(): WidgetConfig {
    if (_cfg) return _cfg
    return {
        cartBackendBaseUrl: '',
        editorBaseUrl: DEFAULT_EDITOR_BASE_URL,
        graphqlUrl: DEFAULT_GRAPHQL_URL,
        overviewUrl: DEFAULT_OVERVIEW_URL,
        logoUrl: DEFAULT_LOGO_URL,
    }
}
